import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Building2, Upload, Trash2, RotateCcw, Globe2 } from 'lucide-react'
import {
  Select, apiGet, adminMutate, adminUpload, usePermissions,
} from './primitives.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { useSaveBar } from '../shell/editing.js'
import { syncFromServer, type Prefs } from '../prefs.js'
import { Mark } from '../Logo.js'
import { Row, Segmented, AccentPicker } from './appearance-controls.js'
import { CropperModal } from './Cropper.js'
import { SafeModeSettings } from './SafeModeSettings.js'
import { uploadPath, type CropFraction } from './media-staging.js'

/**
 * /settings/general (LOT 5.5.1) — TOUT ce qui concerne L'ENTREPRISE :
 *
 *   · identité de l'instance (nom, organisation, titre navigateur dérivé)
 *   · branding (logo, logo compact, favicon, touch icon)
 *   · apparence PAR DÉFAUT héritée par tout le monde
 *   · navigation par défaut (sidebar, page de démarrage)
 *   · régional (fuseau, formats)
 *
 * /settings/appearance ne contient plus que MES préférences.
 *
 * UNE unité d'édition pour toute la page : la Save Bar globale enregistre
 * l'ensemble cohérent (identité + défauts + assets stagés) en une passe.
 * Un upload choisi mais non enregistré est un STAGING local (aperçu objet
 * URL) : l'asset définitif n'est activé qu'après Enregistrer ; Annuler
 * détruit le staging et garde l'asset précédent.
 */

interface AdminAppearance {
  identity: { app_name: string; org_name: string }
  org_defaults: Prefs
  assets: { kind: string; mime: string; size_bytes: number; width: number | null; height: number | null; sha256: string }[]
}

type Staged = { file: File; url: string; crop?: CropFraction } | 'delete'

/** Cibles carrées : proposent le cadrage avant staging. */
const CROP_KINDS = new Set(['logo_compact', 'touch_icon'])

const ASSET_KINDS = [
  { kind: 'logo', label: 'Logo principal', hint: 'Sidebar étendue, login. Grande image acceptée : réduite automatiquement (ratio préservé).' },
  { kind: 'logo_compact', label: 'Logo compact', hint: 'Sidebar repliée. Cadrage carré proposé à l’upload.' },
  { kind: 'favicon', label: 'Favicon', hint: 'Onglet navigateur. PNG (réduit automatiquement) ou ICO.' },
  { kind: 'touch_icon', label: 'Apple Touch Icon', hint: 'Raccourci iOS. Cadrage carré proposé à l’upload.' },
]

const sameRecord = (a: Record<string, string>, b: Record<string, string>) =>
  Object.keys(a).length === Object.keys(b).length && Object.entries(a).every(([k, v]) => b[k] === v)

export function GeneralPage() {
  const { permissions, authenticated } = usePermissions()
  const canAdmin = authenticated && permissions.includes('appearance.write')

  const [admin, setAdmin] = useState<AdminAppearance | null>(null)
  const [identity, setIdentity] = useState<{ app_name: string; org_name: string } | null>(null)
  const [defaults, setDefaults] = useState<Prefs | null>(null)
  const [staged, setStaged] = useState<Record<string, Staged | undefined>>({})
  const [cropping, setCropping] = useState<{ kind: string; file: File } | null>(null)
  const [cropOpen, setCropOpen] = useState(false)
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({})

  const charger = () => {
    apiGet<AdminAppearance>('/api/v1/admin/appearance').then((a) => {
      setAdmin(a)
      setIdentity({ ...a.identity })
      setDefaults({ ...a.org_defaults })
    }).catch(() => undefined)
  }
  useEffect(charger, [])

  // Les aperçus stagés (objet URLs) sont libérés quand ils disparaissent.
  const urlsRef = useRef<string[]>([])
  useEffect(() => () => { urlsRef.current.forEach((u) => URL.revokeObjectURL(u)) }, [])

  /* ─── dirty RÉEL : diff avec le snapshot serveur ─── */
  const identityDirty = !!admin && !!identity
    && (identity.app_name !== admin.identity.app_name || identity.org_name !== admin.identity.org_name)
  const defaultsDirty = !!admin && !!defaults && !sameRecord(defaults, admin.org_defaults)
  const stagedCount = Object.values(staged).filter(Boolean).length
  const dirty = identityDirty || defaultsDirty || stagedCount > 0
  const count = (identityDirty ? 1 : 0) + (defaultsDirty ? 1 : 0) + stagedCount

  const invalid = identity && (identity.app_name.trim().length < 1 || identity.org_name.trim().length < 1)
    ? 'Le nom de l’application et l’organisation ne peuvent pas être vides.'
    : null

  useSaveBar({
    dirty: canAdmin && dirty,
    count,
    invalid,
    successMessage: 'Réglages de l’entreprise enregistrés — ils s’appliquent à tout le monde.',
    onSave: async () => {
      if (!admin || !identity || !defaults) return 'Chargement incomplet — rechargez la page.'
      if (identityDirty) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/admin/appearance/identity', {
          app_name: identity.app_name.trim(), org_name: identity.org_name.trim(),
        })
        if (r.status >= 300) return r.data.message ?? 'L’identité n’a pas été enregistrée.'
      }
      if (defaultsDirty) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/admin/appearance/defaults', { defaults })
        if (r.status >= 300) return r.data.message ?? 'Les défauts n’ont pas été enregistrés.'
      }
      for (const [kind, st] of Object.entries(staged)) {
        if (!st) continue
        const r = st === 'delete'
          ? await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/admin/branding/${kind}`)
          : await adminUpload<{ error?: string; message?: string }>('PUT', uploadPath(`/api/v1/admin/branding/${kind}`, st.crop ?? null), st.file)
        if (r.status >= 300) {
          const label = ASSET_KINDS.find((k) => k.kind === kind)?.label ?? kind
          return `${label} : ${r.data.message ?? 'refusé.'}`
        }
      }
      setStaged({})
      charger()
      await syncFromServer()
      return true
    },
    onCancel: () => {
      if (!admin) return
      setIdentity({ ...admin.identity })
      setDefaults({ ...admin.org_defaults })
      setStaged({})
    },
  })

  const assetOf = (kind: string) => admin?.assets.find((a) => a.kind === kind) ?? null
  const previewUrl = (kind: string): string | null => {
    const st = staged[kind]
    if (st === 'delete') return null
    if (st) return st.url
    const a = assetOf(kind)
    return a ? `/api/v1/public/branding/asset/${kind}?v=${a.sha256.slice(0, 16)}` : null
  }

  const stageFile = (kind: string, file: File, crop?: CropFraction) => {
    const url = URL.createObjectURL(file)
    urlsRef.current.push(url)
    setStaged((s) => ({ ...s, [kind]: crop ? { file, url, crop } : { file, url } }))
  }

  const d = defaults ?? {}
  const dval = (k: string, fb: string) => d[k] ?? fb
  const dchange = (patch: Prefs) => { if (canAdmin && defaults) setDefaults({ ...defaults, ...patch }) }

  const fait = (label: string, value: ReactNode) => (
    <Row label={label}><span style={{ fontSize: 'var(--fs-14)', color: 'var(--text-2)' }}>{value}</span></Row>
  )

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Général</h1>
      <p className="lc-subtitle">
        L’identité et les réglages de l’entreprise — hérités par tous les
        comptes tant qu’ils n’ont pas leurs propres préférences.
      </p>

      {!canAdmin ? (
        <div className="adm-note" style={{ maxWidth: 680 }}>
          Lecture seule : modifier ces valeurs demande la permission
          d’administration de l’apparence (opération sensible, confirmée par
          une ré-authentification forte).
        </div>
      ) : null}

      {/* ═══ Mode sûr de Gestion (Mode Sûr V2) ═══ */}
      <SafeModeSettings />

      {/* ═══ Identité de l'instance ═══ */}
      <section className="lc-panel gd-panel" aria-labelledby="gn-id" style={{ marginTop: 'var(--sp-4)' }}>
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="gn-id"><Building2 size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Identité de l’instance</h2>
        </header>
        <div className="lc-panel__body ap-body">
          {admin && identity ? (
            <>
              <Row label="Nom de l’application">
                {canAdmin ? (
                  <input className="ad-input" value={identity.app_name} maxLength={40}
                    onChange={(e) => setIdentity({ ...identity, app_name: e.target.value })} />
                ) : <strong>{admin.identity.app_name}</strong>}
              </Row>
              <Row label="Organisation / sous-titre">
                {canAdmin ? (
                  <input className="ad-input" value={identity.org_name} maxLength={60}
                    onChange={(e) => setIdentity({ ...identity, org_name: e.target.value })} />
                ) : <strong>{admin.identity.org_name}</strong>}
              </Row>
              <Row label="Titre du navigateur" hint="Dérivé automatiquement — ce n’est pas un champ à remplir.">
                <span className="lc-pill lc-pill--idle" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-12)' }}>
                  {identity.app_name || '…'} — {identity.org_name || '…'}
                </span>
              </Row>
            </>
          ) : <p className="lc-card__desc">Chargement…</p>}
        </div>
      </section>

      {/* ═══ Branding ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Branding">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Branding</h2>
          <span className="lc-panel__spacer" />
          {canAdmin && admin?.assets.length ? (
            <button className="lc-btn lc-btn--ghost" title="Marquer tous les assets pour suppression — appliqué à l’enregistrement"
              onClick={() => setStaged((s) => {
                const next = { ...s }
                for (const k of ASSET_KINDS) { if (assetOf(k.kind)) next[k.kind] = 'delete' }
                return next
              })}>
              <RotateCcw size={14} /> Restaurer LIVE CONTROL
            </button>
          ) : null}
        </header>
        <div className="lc-panel__body ap-body">
          <div className="ap-assets">
            {ASSET_KINDS.map((k) => {
              const a = assetOf(k.kind)
              const st = staged[k.kind]
              const url = previewUrl(k.kind)
              return (
                <div key={k.kind} className="ap-asset">
                  <div className="ap-asset__preview">
                    {url ? <img src={url} alt={k.label} /> : k.kind === 'favicon' || k.kind === 'logo_compact'
                      ? <span className="ap-asset__default"><Mark size={26} /></span>
                      : <span className="ap-asset__default ap-asset__default--wide"><Mark size={22} /> LIVE CONTROL</span>}
                  </div>
                  <div className="ap-asset__meta">
                    <div className="ap-asset__name">
                      {k.label}
                      {st ? <span className="lc-pill lc-pill--warning" style={{ marginLeft: 8, fontSize: 11 }}>{st === 'delete' ? 'Suppression à enregistrer' : 'À enregistrer'}</span> : null}
                    </div>
                    <div className="ap-asset__hint">
                      {st && st !== 'delete'
                        ? `${st.file.name} · ${Math.round(st.file.size / 1024)} Kio (aperçu local)`
                        : a ? `${a.mime.replace('image/', '').toUpperCase()} · ${a.width ?? '?'}×${a.height ?? '?'} · ${Math.round(a.size_bytes / 1024)} Kio` : k.hint}
                    </div>
                  </div>
                  {canAdmin ? (
                    <div className="ap-asset__actions">
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,.ico" hidden
                        ref={(el) => { fileInputs.current[k.kind] = el }}
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          e.target.value = ''
                          if (!f) return
                          // Cibles carrées : cadrage proposé (jamais pour le
                          // logo horizontal principal ni l'ICO).
                          if (CROP_KINDS.has(k.kind) && f.type !== 'image/x-icon') {
                            setCropping({ kind: k.kind, file: f }); setCropOpen(true)
                          } else stageFile(k.kind, f)
                        }} />
                      <button className="lc-btn" onClick={() => fileInputs.current[k.kind]?.click()}>
                        <Upload size={14} /> {a || st ? 'Remplacer' : 'Choisir'}
                      </button>
                      {(a && st !== 'delete') || (st && st !== 'delete') ? (
                        <button className="lc-btn lc-btn--ghost" title="Revenir au visuel par défaut (appliqué à l’enregistrement)"
                          onClick={() => setStaged((s) => ({ ...s, [k.kind]: a ? 'delete' : undefined }))}>
                          <Trash2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
          <div className="adm-note">
            SVG refusé volontairement (pas de code exécutable en branding) —
            PNG, JPEG, WebP, ICO. Le contenu du fichier fait foi, pas son nom.
            Les assets vivent dans PostgreSQL : couverts par les sauvegardes et
            le test de restauration. Rien n’est activé avant l’enregistrement.
          </div>
        </div>
      </section>

      {/* ═══ Apparence par défaut ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Apparence par défaut">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Apparence par défaut de l’entreprise</h2>
        </header>
        <div className="lc-panel__body ap-body">
          <div className="adm-note" style={{ marginTop: 0 }}>
            Ces valeurs s’appliquent à tout le monde (écran de connexion
            compris). Chaque compte peut ensuite les surcharger dans
            Paramètres → Apparence.
          </div>
          {defaults ? (
            <>
              <Row label="Thème par défaut">
                <Segmented ariaLabel="Thème par défaut" value={dval('theme', 'dark')} onChange={(v) => dchange({ theme: v })} disabled={!canAdmin}
                  options={[{ value: 'system', label: 'Système' }, { value: 'dark', label: 'Sombre' }, { value: 'light', label: 'Clair' }]} />
              </Row>
              <Row label="Accent par défaut">
                <AccentPicker value={dval('accent', 'violet')} custom={dval('accent_custom', '#7C5CFF')} disabled={!canAdmin}
                  onChange={(accent, custom) => dchange(custom ? { accent, accent_custom: custom } : { accent })} />
              </Row>
              <Row label="Ambiance par défaut">
                <Segmented ariaLabel="Ambiance par défaut" value={dval('ambience', 'premium')} onChange={(v) => dchange({ ambience: v })} disabled={!canAdmin}
                  options={[{ value: 'minimal', label: 'Minimale' }, { value: 'discrete', label: 'Discrète' }, { value: 'premium', label: 'Premium' }, { value: 'immersive', label: 'Immersive' }]} />
              </Row>
              <Row label="Mouvement par défaut">
                <Segmented ariaLabel="Mouvement par défaut" value={dval('motion', 'system')} onChange={(v) => dchange({ motion: v })} disabled={!canAdmin}
                  options={[{ value: 'system', label: 'Système' }, { value: 'full', label: 'Complet' }, { value: 'reduced', label: 'Réduit' }, { value: 'minimal', label: 'Minimal' }]} />
              </Row>
              <Row label="Densité par défaut">
                <Segmented ariaLabel="Densité par défaut" value={dval('density', 'comfortable')} onChange={(v) => dchange({ density: v })} disabled={!canAdmin}
                  options={[{ value: 'comfortable', label: 'Confortable' }, { value: 'compact', label: 'Compacte' }]} />
              </Row>
              <Row label="Échelle par défaut">
                <Segmented ariaLabel="Échelle par défaut" value={dval('scale', '100')} onChange={(v) => dchange({ scale: v })} disabled={!canAdmin}
                  options={[{ value: '90', label: '90 %' }, { value: '100', label: '100 %' }, { value: '110', label: '110 %' }, { value: '120', label: '120 %' }]} />
              </Row>
              <Row label="Rayon par défaut">
                <Segmented ariaLabel="Rayon par défaut" value={dval('radius', 'standard')} onChange={(v) => dchange({ radius: v })} disabled={!canAdmin}
                  options={[{ value: 'compact', label: 'Compact' }, { value: 'standard', label: 'Standard' }, { value: 'round', label: 'Arrondi' }]} />
              </Row>

              <div className="ap-group">Navigation par défaut</div>
              <Row label="Menu par défaut">
                <Segmented ariaLabel="Menu par défaut" value={dval('sidebar', 'auto') === 'pinned' ? 'pinned' : 'auto'} onChange={(v) => dchange({ sidebar: v })} disabled={!canAdmin}
                  options={[{ value: 'auto', label: 'Flottant' }, { value: 'pinned', label: 'Épinglé' }]} />
              </Row>
              <Row label="Menu clic droit par défaut" hint="Chaque compte peut le changer dans Apparence.">
                <Segmented ariaLabel="Menu clic droit par défaut" value={dval('context_menu', 'on')} onChange={(v) => dchange({ context_menu: v })} disabled={!canAdmin}
                  options={[{ value: 'on', label: 'Activé' }, { value: 'off', label: 'Menu du navigateur' }]} />
              </Row>
              <Row label="Page de démarrage par défaut">
                {canAdmin ? (
                  <Select value={dval('home', 'dashboard')} onChange={(v) => dchange({ home: v })} compact
                    options={[
                      { value: 'dashboard', label: 'Tableau de bord' }, { value: 'rh-access', label: 'RH' },
                      { value: 'security', label: 'Sécurité' }, { value: 'parc', label: 'Parc' },
                      { value: 'communication', label: 'Communication' }, { value: 'infrastructure', label: 'Infrastructure' },
                    ]} />
                ) : <strong>{dval('home', 'dashboard')}</strong>}
              </Row>

              <div className="ap-group"><Globe2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Régional</div>
              {fait('Fuseau horaire métier', <span className="ad-mono">Europe/Paris — fixé par l’exploitation, pas un réglage d’interface</span>)}
              <Row label="Format d’heure par défaut">
                <Segmented ariaLabel="Heure par défaut" value={dval('hour_format', '24')} onChange={(v) => dchange({ hour_format: v })} disabled={!canAdmin}
                  options={[{ value: '24', label: '24 h' }, { value: '12', label: '12 h' }]} />
              </Row>
              <Row label="Format de date par défaut">
                <Segmented ariaLabel="Date par défaut" value={dval('date_format', 'short')} onChange={(v) => dchange({ date_format: v })} disabled={!canAdmin}
                  options={[{ value: 'short', label: '21/08/2026' }, { value: 'long', label: '21 août 2026' }, { value: 'iso', label: '2026-08-21' }]} />
              </Row>
            </>
          ) : null}
        </div>
      </section>
      {cropping ? (
        <CropperModal
          open={cropOpen}
          file={cropping.file}
          title={cropping.kind === 'logo_compact' ? 'Cadrer le logo compact' : 'Cadrer l’icône'}
          onClose={() => setCropOpen(false)}
          onExited={() => setCropping(null)}
          onDone={(crop) => {
            stageFile(cropping.kind, cropping.file, crop)
            setCropOpen(false)
          }}
        />
      ) : null}
    </main>
  )
}
